// ABOUTME: Zod validation schemas for TechLog scheduled maintenance items, aligned to ScheduledMxItem API spec.
import { ZdateTime } from '../common.zod';
import { z } from 'zod';

// TechLog ScheduledMxItem response schema — matches the full API response
export const ZTechLogScheduledMXItem = z.object({
  id: z
    .string()
    .describe('Unique identifier for the scheduled maintenance item'),
  aircraftId: z
    .string()
    .describe('ID of the aircraft this maintenance item applies to'),
  packageId: z
    .string()
    .nullable()
    .describe('ID of the maintenance package this item belongs to'),
  mxType: z
    .string()
    .nullable()
    .describe(
      'Maintenance type (parsed from compound {area}_{type} input format)',
    ),
  mxArea: z
    .string()
    .nullable()
    .describe(
      'Maintenance area (parsed from compound {area}_{type} input format)',
    ),
  draft: z.boolean().describe('Whether this is a draft maintenance item'),
  status: z
    .enum([
      'open',
      'resolved',
      'overdue',
      'critical',
      'draft',
      'resolution_pending',
    ])
    .describe('Current status of the scheduled MX item'),
  source: z
    .enum(['amos', 'camp', 'core', 'dashboard', 'techlog', 'api'])
    .describe('Source system that created this maintenance item'),
  details: z
    .string()
    .describe(
      'Shared field between defects and scheduled MX items. Not displayed on TechLog dashboard for MX items — only name is visible. Avoid relying on this field for MX item integrations.',
    ),
  name: z.string().describe('Name or title of the maintenance item'),
  number: z.number().nullable().describe('Maintenance item number or sequence'),
  dueDate: z
    .string()
    .nullable()
    .describe('Date when the maintenance is due (date-only)'),
  dueDateTime: z
    .string()
    .nullable()
    .describe('Date and time when the maintenance is due'),
  estimatedDueDate: z.string().nullable().describe('Estimated due date'),
  dueFlightSeconds: z
    .number()
    .nullable()
    .describe('Flight time threshold in seconds when maintenance is due'),
  dueApuSeconds: z
    .number()
    .nullable()
    .describe('APU runtime threshold in seconds when maintenance is due'),
  dueCycles: z
    .number()
    .nullable()
    .describe('Number of cycles when maintenance is due'),
  dueLandings: z
    .number()
    .nullable()
    .describe('Number of landings when maintenance is due'),
  repeatFlightSeconds: z
    .number()
    .nullable()
    .describe('Flight time interval for recurring maintenance'),
  toleranceFlightSeconds: z
    .number()
    .nullable()
    .describe('Allowable tolerance in flight time'),
  visibleToleranceFlightSeconds: z
    .number()
    .describe('Visible tolerance threshold for flight time'),
  thresholdFlightSeconds: z
    .number()
    .nullable()
    .describe('Warning threshold for flight time before due'),
  repeatApuSeconds: z
    .number()
    .nullable()
    .describe('APU runtime interval for recurring maintenance'),
  toleranceApuSeconds: z
    .number()
    .nullable()
    .describe('Allowable tolerance in APU time'),
  visibleToleranceApuSeconds: z
    .number()
    .describe('Visible tolerance threshold for APU time'),
  thresholdApuSeconds: z
    .number()
    .nullable()
    .describe('Warning threshold for APU time before due'),
  repeatDays: z
    .number()
    .nullable()
    .describe('Calendar day interval for recurring maintenance'),
  toleranceDays: z.number().nullable().describe('Allowable tolerance in days'),
  visibleToleranceDays: z
    .number()
    .describe('Visible tolerance threshold for calendar days'),
  thresholdDays: z
    .number()
    .nullable()
    .describe('Warning threshold for calendar days before due'),
  repeatMonths: z
    .number()
    .nullable()
    .describe('Monthly interval for recurring maintenance'),
  toleranceMonths: z
    .number()
    .nullable()
    .describe('Allowable tolerance in months'),
  visibleToleranceMonths: z
    .number()
    .describe('Visible tolerance threshold for months'),
  thresholdMonths: z
    .number()
    .nullable()
    .describe('Warning threshold for months before due'),
  repeatCycles: z
    .number()
    .nullable()
    .describe('Cycle interval for recurring maintenance'),
  toleranceCycles: z
    .number()
    .nullable()
    .describe('Allowable tolerance in cycles'),
  visibleToleranceCycles: z
    .number()
    .describe('Visible tolerance threshold for cycles'),
  thresholdCycles: z
    .number()
    .nullable()
    .describe('Warning threshold for cycles before due'),
  repeatRemarks: z
    .string()
    .nullable()
    .describe('Additional notes about recurring maintenance schedule'),
  partNumber: z
    .string()
    .nullable()
    .describe('Part number for components involved in maintenance'),
  partSerial: z
    .string()
    .nullable()
    .describe('Serial number of the part being maintained'),
  reference: z
    .string()
    .nullable()
    .describe('Reference number or identifier for the maintenance procedure'),
  assembly: z
    .string()
    .nullable()
    .describe('Aircraft assembly or system involved'),
  ataChapter: z.number().nullable().describe('ATA chapter number'),
  ataSection: z.number().nullable().describe('ATA section number'),
  customData: z.record(z.unknown()).describe('Custom data fields'),
  operatorReference: z
    .string()
    .nullable()
    .describe('Operator-specific reference or identifier'),
  flightsDue: z
    .number()
    .nullable()
    .describe('Number of flights until maintenance is due'),
  placardProcedureCompleted: z
    .boolean()
    .nullable()
    .describe('Whether placard procedure has been completed'),
  operationalProcedureCompleted: z
    .boolean()
    .nullable()
    .describe('Whether operational procedure has been completed'),
  crewActionable: z
    .boolean()
    .describe('Whether this maintenance item requires crew action'),
  serviceabilityLimiting: z
    .boolean()
    .describe('Whether this maintenance item limits aircraft serviceability'),
  normalisedFlightSecondsDue: z
    .number()
    .nullable()
    .describe('Normalized flight time when maintenance is due'),
  normalisedCyclesDue: z
    .number()
    .nullable()
    .describe('Normalized cycles count when maintenance is due'),
  unitOfTime: z
    .enum(['days', 'months'])
    .describe('Unit of time for scheduling calculations'),
  importedAt: z
    .string()
    .nullable()
    .describe('Timestamp when this maintenance item was imported'),
  editedBy: z
    .string()
    .nullable()
    .describe('ID of user who last edited this maintenance item'),
  eropsPrevented: z
    .boolean()
    .nullable()
    .describe('Whether this maintenance prevents ETOPS operations'),
  placardNumber: z
    .string()
    .nullable()
    .describe('Placard identification number'),
  editedDate: z
    .string()
    .nullable()
    .describe('Date when the item was last edited'),
  campSyncDate: z.string().nullable().describe('CAMP synchronization date'),
  calendarDaysAmberLimit: z
    .number()
    .nullable()
    .describe('Calendar days threshold for amber warning status'),
  calendarSecondsAmberLimit: z
    .number()
    .nullable()
    .describe('Calendar seconds threshold for amber warning status'),
  thirdPartyReferences: z
    .record(z.unknown())
    .describe('References from third party integrations'),
  createdAt: ZdateTime.describe('Timestamp when this item was created'),
  updatedAt: ZdateTime.describe('Timestamp when this item was last updated'),
});

// TechLogScheduledMXItemCreateInput schema — matches TechLog ScheduledMxItemBody spec
export const ZTechlogScheduledMXItemCreateInput = z.object({
  aircraftId: z
    .string()
    .optional()
    .describe('ID of the aircraft this maintenance item applies to'),
  packageId: z
    .string()
    .optional()
    .describe('ID of the maintenance package this item belongs to'),
  draft: z
    .boolean()
    .optional()
    .describe('Whether this is a draft maintenance item'),
  source: z
    .string()
    .optional()
    .describe(
      'Source system. TechLog accepts: amos, camp, core, dashboard, techlog, api. Non-enum values are silently replaced with api.',
    ),
  details: z
    .string()
    .describe(
      'Shared field between defects and scheduled MX items. Not displayed on TechLog dashboard for MX items — only name is visible. Avoid relying on this field for MX item integrations.',
    ),
  name: z.string().describe('Name or title of the maintenance item'),
  number: z.number().optional().describe('Maintenance item number or sequence'),
  dueDate: ZdateTime.optional().describe(
    'Date when the maintenance is due. Use this in preference to dueDateTime unless time is needed. Only one of dueDate and dueDateTime can be set.',
  ),
  dueDateTime: ZdateTime.optional().describe(
    'Date and time when the maintenance is due. Only one of dueDate and dueDateTime can be set.',
  ),
  estimatedDate: ZdateTime.optional().describe('Estimated completion date'),
  dueFlightSeconds: z
    .number()
    .optional()
    .describe('Flight time threshold in seconds when maintenance is due'),
  dueApuSeconds: z
    .number()
    .optional()
    .describe('APU runtime threshold in seconds when maintenance is due'),
  dueCycles: z
    .number()
    .optional()
    .describe('Number of cycles when maintenance is due'),
  dueLandings: z
    .number()
    .optional()
    .describe('Number of landings when maintenance is due'),
  repeatFlightSeconds: z
    .number()
    .optional()
    .describe('Flight time interval for recurring maintenance'),
  toleranceFlightSeconds: z
    .number()
    .optional()
    .describe(
      'Allowable tolerance in flight time before maintenance is overdue',
    ),
  visibleToleranceFlightSeconds: z
    .number()
    .optional()
    .describe('Visible tolerance threshold for flight time'),
  thresholdFlightSeconds: z
    .number()
    .optional()
    .describe('Warning threshold for flight time before due'),
  repeatApuSeconds: z
    .number()
    .optional()
    .describe('APU runtime interval for recurring maintenance'),
  toleranceApuSeconds: z
    .number()
    .optional()
    .describe('Allowable tolerance in APU time before maintenance is overdue'),
  visibleToleranceApuSeconds: z
    .number()
    .optional()
    .describe('Visible tolerance threshold for APU time'),
  thresholdApuSeconds: z
    .number()
    .optional()
    .describe('Warning threshold for APU time before due'),
  repeatDays: z
    .number()
    .optional()
    .describe('Calendar day interval for recurring maintenance'),
  toleranceDays: z
    .number()
    .optional()
    .describe('Allowable tolerance in days before maintenance is overdue'),
  visibleToleranceDays: z
    .number()
    .optional()
    .describe('Visible tolerance threshold for calendar days'),
  thresholdDays: z
    .number()
    .optional()
    .describe('Warning threshold for calendar days before due'),
  repeatMonths: z
    .number()
    .optional()
    .describe('Monthly interval for recurring maintenance'),
  toleranceMonths: z
    .number()
    .optional()
    .describe('Allowable tolerance in months before maintenance is overdue'),
  visibleToleranceMonths: z
    .number()
    .optional()
    .describe('Visible tolerance threshold for months'),
  thresholdMonths: z
    .number()
    .optional()
    .describe('Warning threshold for months before due'),
  repeatCycles: z
    .number()
    .optional()
    .describe('Cycle interval for recurring maintenance'),
  toleranceCycles: z
    .number()
    .optional()
    .describe('Allowable tolerance in cycles before maintenance is overdue'),
  thresholdCycles: z
    .number()
    .optional()
    .describe('Warning threshold for cycles before due'),
  repeatRemarks: z
    .string()
    .optional()
    .describe('Additional notes about recurring maintenance schedule'),
  partNumber: z
    .string()
    .optional()
    .describe('Part number for components involved in maintenance'),
  partSerial: z
    .string()
    .optional()
    .describe('Serial number of the part being maintained'),
  reference: z
    .string()
    .optional()
    .describe('Reference number or identifier for the maintenance procedure'),
  ataChapter: z
    .number()
    .optional()
    .describe('ATA (Air Transport Association) chapter number'),
  ataSection: z
    .number()
    .optional()
    .describe('ATA (Air Transport Association) section number'),
  assembly: z
    .string()
    .optional()
    .describe('Aircraft assembly or system involved'),
  customData: z
    .record(z.unknown())
    .optional()
    .describe('Custom data fields for additional maintenance information'),
  operatorReference: z
    .string()
    .optional()
    .describe('Operator-specific reference or identifier'),
  placardProcedureCompleted: z
    .boolean()
    .optional()
    .describe('Whether placard procedure has been completed'),
  operationalProcedureCompleted: z
    .boolean()
    .optional()
    .describe('Whether operational procedure has been completed'),
  crewActionable: z
    .boolean()
    .optional()
    .describe('Whether this maintenance item requires crew action'),
  serviceabilityLimiting: z
    .boolean()
    .optional()
    .describe('Whether this maintenance item limits aircraft serviceability'),
  normalisedFlightSecondsDue: z
    .number()
    .optional()
    .describe('Normalized flight time when maintenance is due'),
  normalisedCyclesDue: z
    .number()
    .optional()
    .describe('Normalized cycles count when maintenance is due'),
  unitOfTime: z
    .enum(['days', 'months'])
    .optional()
    .describe('Unit of time for scheduling calculations'),
  importedAt: ZdateTime.optional().describe(
    'Timestamp when this maintenance item was imported',
  ),
  editedBy: z
    .string()
    .optional()
    .describe('ID of user who last edited this maintenance item'),
  eropsPrevented: z
    .boolean()
    .optional()
    .describe(
      'Whether this maintenance prevents ETOPS (Extended-range Twin-engine Operational Performance Standards) operations',
    ),
  placardNumber: z
    .string()
    .optional()
    .describe('Placard identification number'),
  calendarDaysAmberLimit: z
    .number()
    .optional()
    .describe(
      'Calendar days threshold for amber warning status. Only one of calendarDaysAmberLimit and calendarDaysSecondsLimit can be set.',
    ),
  calendarDaysSecondsLimit: z
    .number()
    .optional()
    .describe(
      'Calendar days limit in seconds. Only one of calendarDaysAmberLimit and calendarDaysSecondsLimit can be set.',
    ),
  // Undocumented but required — TechLog expects compound {area}_{type} format (e.g. 'airframe_scheduled').
  // Bare type values like 'scheduled' cause TechLog to return 500.
  mxType: z
    .string()
    .optional()
    .describe(
      'Maintenance type in compound {area}_{type} format (e.g. airframe_scheduled). Not in API spec but required for proper categorization.',
    ),
});

// Export type assertions for TypeScript integration
export type ZTechLogScheduledMXItemType = z.infer<
  typeof ZTechLogScheduledMXItem
>;
export type ZTechlogScheduledMXItemCreateInputType = z.infer<
  typeof ZTechlogScheduledMXItemCreateInput
>;
